import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Users, Trash2, UserPlus, Check, X, Eye, FolderOpen, RefreshCw, Bell, Search, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { teamWorkspaceService } from '../lib/teamWorkspaceService';
import { userTierService } from '../lib/userTierService';
import { ConfirmModal } from '../components/ConfirmModal';
import { useTeams, useCreateTeam, useDeleteTeam, useLeaveTeam, useAcceptInvitation, useDeclineInvitation, useInviteTeamMember } from '../hooks/useTeams';
import { useTeamWorkspaces, useCreateWorkspace } from '../hooks/useWorkspaces';
import { TeamMembersModal } from '../components/TeamMembersModal';
import { UserTeam, Team, Workspace } from '../types';

export const TeamsPage: React.FC = () => {
  const navigate = useNavigate();
  
  // React Query hooks
  const { data: teams = [], isLoading: loading, error: teamsError, refetch: refetchTeams } = useTeams();
  const createTeamMutation = useCreateTeam();
  const deleteTeamMutation = useDeleteTeam();
  const leaveTeamMutation = useLeaveTeam();
  const acceptInvitationMutation = useAcceptInvitation();
  const declineInvitationMutation = useDeclineInvitation();
  const inviteMemberMutation = useInviteTeamMember();
  
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const { data: selectedTeamWorkspaces = [], isLoading: workspacesLoading, refetch: refetchWorkspaces } = useTeamWorkspaces(selectedTeam?.id || null);
  const createWorkspaceMutation = useCreateWorkspace();
  
  const [error, setError] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCreateWorkspaceModal, setShowCreateWorkspaceModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showWorkspaces, setShowWorkspaces] = useState(false);
  const [showProGate, setShowProGate] = useState(false);
  const [userTier, setUserTier] = useState<string>('free');
  const [tierLoading, setTierLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean; action: (() => void) | null; title: string; message: string }>({
    isOpen: false,
    action: null,
    title: '',
    message: ''
  });
  const [searchQuery, setSearchQuery] = useState('');

  // Form states
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDescription, setNewTeamDescription] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'admin' | 'viewer'>('member');
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspaceDescription, setNewWorkspaceDescription] = useState('');
  const [newWorkspaceDocketNumber, setNewWorkspaceDocketNumber] = useState('');

  useEffect(() => {
    const checkUserTier = async () => {
      try {
        const tier = await userTierService.getUserTier();
        setUserTier(tier);
      } catch (error) {
        // Error handled silently
      } finally {
        setTierLoading(false);
      }
    };

    checkUserTier();
  }, []);

  const isProOrAbove = userTier === 'pro' || userTier === 'enterprise' || userTier === 'paid';

  const handleCreateTeamClick = () => {
    if (!isProOrAbove) {
      setShowProGate(true);
    } else {
      setShowCreateModal(true);
    }
  };

  // Handle errors from React Query
  useEffect(() => {
    if (teamsError) {
      setError(teamsError instanceof Error ? teamsError.message : 'Failed to load teams');
    }
  }, [teamsError]);

  // Filter teams based on search query
  const filteredTeams = useMemo(() => {
    if (!searchQuery.trim()) return teams;
    const query = searchQuery.toLowerCase();
    return teams.filter(team => 
      team.team_name.toLowerCase().includes(query) ||
      (team.team_description && team.team_description.toLowerCase().includes(query))
    );
  }, [teams, searchQuery]);

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) {
      setError('Team name is required');
      return;
    }

    try {
      setError('');
      await createTeamMutation.mutateAsync({
        name: newTeamName.trim(),
        description: newTeamDescription.trim() || undefined
      });
      
      setNewTeamName('');
      setNewTeamDescription('');
      setShowCreateModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create team');
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim() || !selectedTeam) {
      setError('Email and team selection are required');
      return;
    }

    try {
      setError('');
      await inviteMemberMutation.mutateAsync({
        teamId: selectedTeam.id,
        userEmail: inviteEmail.trim(),
        role: inviteRole
      });
      
      setInviteEmail('');
      setInviteRole('member');
      setShowInviteModal(false);
      setSelectedTeam(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite member');
    }
  };

  const handleViewWorkspaces = (team: Team) => {
    setSelectedTeam(team);
    setShowWorkspaces(true);
  };

  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName.trim() || !selectedTeam) {
      setError('Workspace name and team selection are required');
      return;
    }

    if (!newWorkspaceDocketNumber.trim()) {
      setError('Docket number is required');
      return;
    }

    // Validate docket number format
    if (!/^[A-Z0-9\-_\.]+$/i.test(newWorkspaceDocketNumber.trim())) {
      setError('Docket number can only contain letters, numbers, hyphens, underscores, and periods');
      return;
    }

    try {
      setError('');
      await createWorkspaceMutation.mutateAsync({
        teamId: selectedTeam.id,
        name: newWorkspaceName.trim(),
        description: newWorkspaceDescription.trim() || undefined,
        docketNumber: newWorkspaceDocketNumber.trim()
      });
      
      setNewWorkspaceName('');
      setNewWorkspaceDescription('');
      setNewWorkspaceDocketNumber('');
      setShowCreateWorkspaceModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create workspace');
    }
  };

  const handleLeaveTeam = async (teamId: string) => {
    setConfirmModal({
      isOpen: true,
      action: async () => {
        try {
          setError('');
          await leaveTeamMutation.mutateAsync(teamId);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to leave team');
        }
      },
      title: 'Leave Team',
      message: 'Are you sure you want to leave this team? You will lose access to all team workspaces.'
    });
  };

  const handleDeleteTeam = async (teamId: string) => {
    setConfirmModal({
      isOpen: true,
      action: async () => {
        try {
          setError('');
          await deleteTeamMutation.mutateAsync(teamId);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to delete team');
        }
      },
      title: 'Delete Team',
      message: 'Are you sure you want to delete this team? This action cannot be undone and will delete all team workspaces.'
    });
  };

  const handleAcceptInvitation = async (teamId: string) => {
    try {
      setError('');
      await acceptInvitationMutation.mutateAsync(teamId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
    }
  };

  const handleDeclineInvitation = async (teamId: string) => {
    try {
      setError('');
      await declineInvitationMutation.mutateAsync(teamId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to decline invitation');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-blue-100 text-blue-800';
      case 'member': return 'bg-green-100 text-green-800';
      case 'viewer': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'invited': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Show loading state while checking user tier
  if (tierLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-3xl font-bold text-gray-900">Teams</h1>
                {teams.filter(t => t.member_status === 'invited').length > 0 && (
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full flex items-center space-x-1">
                    <Bell className="w-4 h-4" />
                    <span>{teams.filter(t => t.member_status === 'invited').length} pending invitation{teams.filter(t => t.member_status === 'invited').length !== 1 ? 's' : ''}</span>
                  </span>
                )}
              </div>
              <p className="text-gray-600 mt-2">
                Create teams to quickly add the same collaborators to multiple workspaces
              </p>
              <div className="mt-2">
                <button
                  onClick={() => navigate('/workspaces')}
                  className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                >
                  ← Back to Workspaces
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/workspaces')}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium rounded-lg transition-colors"
              >
                Workspaces
              </button>
              <button
                onClick={handleCreateTeamClick}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Create Team</span>
              </button>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && !showProGate && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Pending Invitations */}
        {!loading && teams.filter(t => t.member_status === 'invited').length > 0 && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg shadow">
            <div className="px-6 py-4 border-b border-yellow-200">
              <h2 className="text-lg font-medium text-yellow-900 flex items-center space-x-2">
                <Bell className="w-5 h-5" />
                <span>Pending Invitations</span>
              </h2>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                {teams.filter(t => t.member_status === 'invited').map((team) => (
                  <div key={team.team_id} className="p-4 border border-yellow-300 rounded-lg bg-white">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-medium text-gray-900">{team.team_name}</h3>
                          <span className={`px-2 py-1 text-xs rounded-full ${getRoleBadgeColor(team.user_role)}`}>
                            {team.user_role}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeColor(team.member_status)}`}>
                            {team.member_status}
                          </span>
                        </div>
                        
                        {team.team_description && (
                          <p className="text-gray-600 mb-2">{team.team_description}</p>
                        )}
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <div className="flex items-center space-x-1">
                            <Users className="w-4 h-4" />
                            <span>{team.member_count} members</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleAcceptInvitation(team.team_id)}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md transition-colors flex items-center space-x-2"
                        >
                          <Check className="w-4 h-4" />
                          <span>Accept</span>
                        </button>
                        <button
                          onClick={() => handleDeclineInvitation(team.team_id)}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md transition-colors flex items-center space-x-2"
                        >
                          <X className="w-4 h-4" />
                          <span>Decline</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Teams List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">Your Teams</h2>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search teams..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
          </div>
          
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading your teams...</p>
              </div>
            ) : teams.filter(t => t.member_status !== 'invited').length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No teams yet</p>
                <p className="text-sm text-gray-500 mt-1">Create your first team to start collaborating</p>
              </div>
            ) : (
              <div className="space-y-4">
                {teams.filter(t => t.member_status !== 'invited').map((team) => (
                  <div key={team.team_id} className="p-4 border border-gray-200 rounded-lg hover:border-orange-300 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-medium text-gray-900">{team.team_name}</h3>
                          <span className={`px-2 py-1 text-xs rounded-full ${getRoleBadgeColor(team.user_role)}`}>
                            {team.user_role}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeColor(team.member_status)}`}>
                            {team.member_status}
                          </span>
                        </div>
                        
                        {team.team_description && (
                          <p className="text-gray-600 mb-2">{team.team_description}</p>
                        )}
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <div className="flex items-center space-x-1">
                            <Users className="w-4 h-4" />
                            <span>{team.member_count} members</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewWorkspaces({ id: team.team_id, name: team.team_name, description: team.team_description, owner_id: '', created_at: '', updated_at: '' })}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          title="View Workspaces"
                        >
                          <FolderOpen className="w-4 h-4" />
                        </button>
                        
                        <button
                          onClick={() => {
                            setSelectedTeam({ id: team.team_id, name: team.team_name, description: team.team_description, owner_id: '', created_at: '', updated_at: '' });
                            setShowMembersModal(true);
                          }}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                          title="View Members"
                        >
                          <Users className="w-4 h-4" />
                        </button>
                        
                        {team.user_role === 'owner' && (
                          <button
                            onClick={() => {
                              setSelectedTeam({ id: team.team_id, name: team.team_name, description: team.team_description, owner_id: '', created_at: '', updated_at: '' });
                              setShowInviteModal(true);
                            }}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                            title="Invite Member"
                          >
                            <UserPlus className="w-4 h-4" />
                          </button>
                        )}
                        
                        {team.user_role === 'owner' ? (
                          <button
                            onClick={() => handleDeleteTeam(team.team_id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            title="Delete Team"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleLeaveTeam(team.team_id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            title="Leave Team"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Team Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Create New Team</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fld-TeamsPagetsx-0">
                    Team Name *
                  </label>
                  <input
                    type="text"
                    value={newTeamName}
                    id="fld-TeamsPagetsx-0" onChange={(e) => setNewTeamName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter team name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fld-TeamsPagetsx-1">
                    Description
                  </label>
                  <textarea
                    value={newTeamDescription}
                    id="fld-TeamsPagetsx-1" onChange={(e) => setNewTeamDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter team description (optional)"
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTeam}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md transition-colors"
                >
                  Create Team
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Member Modal */}
      {showInviteModal && selectedTeam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Invite Team Member</h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Inviting to: <span className="font-medium">{selectedTeam.name}</span>
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fld-TeamsPagetsx-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={inviteEmail}
                    id="fld-TeamsPagetsx-2" onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter email address"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fld-TeamsPagetsx-3">
                    Role
                  </label>
                  <select
                    value={inviteRole}
                    id="fld-TeamsPagetsx-3" onChange={(e) => setInviteRole(e.target.value as 'member' | 'admin' | 'viewer')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleInviteMember}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md transition-colors"
                >
                  Send Invitation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Workspaces Modal */}
      {showWorkspaces && selectedTeam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Team Workspaces</h3>
                <p className="text-sm text-gray-600">{selectedTeam.name}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => refetchWorkspaces()}
                  disabled={workspacesLoading}
                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm rounded-md transition-colors flex items-center space-x-1"
                  title="Refresh Workspaces"
                >
                  <RefreshCw className={`w-4 h-4 ${workspacesLoading ? 'animate-spin' : ''}`} />
                  <span>{workspacesLoading ? 'Refreshing...' : 'Refresh'}</span>
                </button>
                <button
                  onClick={() => setShowCreateWorkspaceModal(true)}
                  className="px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white text-sm rounded-md transition-colors"
                >
                  Create Workspace
                </button>
                <button
                  onClick={() => {
                    setShowWorkspaces(false);
                    setSelectedTeam(null);
                    setSelectedTeamWorkspaces([]);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {workspacesLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading workspaces...</p>
                </div>
              ) : selectedTeamWorkspaces.length === 0 ? (
                <div className="text-center py-8">
                  <FolderOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No workspaces yet</p>
                  <p className="text-sm text-gray-500 mt-1">Create workspaces to organize docket summaries</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedTeamWorkspaces.map((workspace) => (
                    <div key={workspace.id} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 mb-1">{workspace.name}</h4>
                          {workspace.description && (
                            <p className="text-gray-600 mb-2">{workspace.description}</p>
                          )}
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <span>Docket: {workspace.docket_number}</span>
                            <span>Created: {formatDate(workspace.created_at)}</span>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => {
                            navigate(`/workspace/${workspace.id}`);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          title="View Workspace"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Workspace Modal */}
      {showCreateWorkspaceModal && selectedTeam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Create New Workspace</h3>
              <button
                onClick={() => setShowCreateWorkspaceModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Creating workspace for: <span className="font-medium">{selectedTeam.name}</span>
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fld-TeamsPagetsx-4">
                    Workspace Name *
                  </label>
                  <input
                    type="text"
                    value={newWorkspaceName}
                    id="fld-TeamsPagetsx-4" onChange={(e) => setNewWorkspaceName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter workspace name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fld-TeamsPagetsx-5">
                    Description
                  </label>
                  <textarea
                    value={newWorkspaceDescription}
                    id="fld-TeamsPagetsx-5" onChange={(e) => setNewWorkspaceDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter workspace description (optional)"
                    rows={3}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fld-TeamsPagetsx-6">
                    Docket Number *
                  </label>
                  <input
                    type="text"
                    value={newWorkspaceDocketNumber}
                    id="fld-TeamsPagetsx-6" onChange={(e) => setNewWorkspaceDocketNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter docket number (e.g., 21-402, GN-09-51)"
                    required
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateWorkspaceModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateWorkspace}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md transition-colors"
                >
                  Create Workspace
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, action: null, title: '', message: '' })}
        onConfirm={() => {
          if (confirmModal.action) {
            confirmModal.action();
          }
        }}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Confirm"
        cancelText="Cancel"
      />

      {/* Team Members Modal */}
      <TeamMembersModal
        isOpen={showMembersModal}
        onClose={() => {
          setShowMembersModal(false);
          setSelectedTeam(null);
        }}
        team={selectedTeam}
        onMemberRemoved={() => {
          refetchTeams();
        }}
      />

      {/* Pro Gate Modal */}
      {showProGate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-8 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-green-50 mb-4">
              <Crown className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Pro Required</h3>
            <p className="text-gray-500 text-sm mb-6">
              Teams and team collaboration are available on the Pro plan ($199/month) and above.
              Upgrade to create teams and collaborate with your colleagues.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => { setShowProGate(false); navigate('/pricing'); }}
                className="w-full px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium text-sm"
              >
                View Pricing
              </button>
              <button
                onClick={() => setShowProGate(false)}
                className="w-full px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
